import { ChangeEvent, DragEvent, useId, useRef, useState } from 'react';
import { faArrowUpFromBracket } from '@fortawesome/pro-regular-svg-icons';
import { faCamera, faCloudDownloadAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled, Typography, useMediaQuery } from '@mui/material';
import DefaultButton from '../../../../../components/Accessibility/DefaultButton';
import ModalAction from '../../../../../components/ModalAction';
import ModalFeedBack from '../../../../../components/ModalFeedBack';
import { getFileExtension } from '../../../../../utils/MimeType';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import { TranscricaoAnexo } from '../../transcricaoTypes';
import {
  ANEXO_ACCEPT,
  ANEXO_ACCEPT_IMAGEM,
  ANEXO_ACCEPT_PDF,
  ANEXO_EXTENSIONS_REGEX,
  ANEXO_MIME_TYPES,
  gerarNomeArquivoFoto,
  isArquivoCamera,
  MAX_ANEXO_BYTES,
  MOBILE_BREAKPOINT,
} from '../../transcricaoAnexoUtils';
import TranscricaoAnexosTable from './TranscricaoAnexosTable';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

type ModalActionState = {
  handleShowModal?: () => void;
  textOutlinedButton: string;
  textContainedButton: string;
  title: string;
  subTitle?: string;
  handleOutlinedButton?: () => void;
  handleContainedButton?: () => void;
  icon: 'error' | 'success' | 'warning';
};

type ModalFeedbackState = {
  icon?: 'error' | 'success' | 'warning';
  title?: string;
  textButton?: string;
  subTitle?: string;
  closeAction: () => void;
  showButtonModal: boolean;
  buttonAction?: () => void;
};

const MSG_ERRO_LER = 'Erro ao ler arquivo.';
const MSG_ARQUIVO_DUPLICADO =
  'Este arquivo já foi anexado. Selecione um arquivo diferente.';

const normalizarNomeArquivo = (nome: string): string => nome.trim().toLowerCase();

const anexoJaExiste = (
  nomeArquivo: string,
  tamanhoBytes: number,
  lista: TranscricaoAnexo[],
): boolean => {
  const nomeNorm = normalizarNomeArquivo(nomeArquivo);
  return lista.some(
    (item) =>
      normalizarNomeArquivo(item.nomeArquivo) === nomeNorm
      && item.tamanhoBytes === tamanhoBytes,
  );
};

const lerArquivoComoBase64 = (file: File): Promise<{ prefix: string; base64Data: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      if (!base64String) {
        reject(new Error('Falha ao ler o arquivo.'));
        return;
      }
      const [prefix, base64Data] = base64String.split(',');
      if (!base64Data) {
        reject(new Error('Falha ao converter o arquivo.'));
        return;
      }
      resolve({ prefix, base64Data });
    };
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler o arquivo.'));
    reader.readAsDataURL(file);
  });

export interface TranscricaoAnexosUploadSemValidacaoProps {
  anexos: TranscricaoAnexo[];
  onAnexosChange: (anexos: TranscricaoAnexo[]) => void;
}

const TranscricaoAnexosUploadSemValidacao: React.FC<TranscricaoAnexosUploadSemValidacaoProps> = ({
  anexos,
  onAnexosChange,
}) => {
  const accessibility = useAccessibility();
  const buttonId = useId();
  const pdfButtonId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isMobileLayout = useMediaQuery(MOBILE_BREAKPOINT);

  const [modalAction, setModalAction] = useState<ModalActionState | null>(null);
  const [modalFeedback, setModalFeedback] = useState<ModalFeedbackState | null>(null);

  const closeModalFeedback = () => {
    setModalFeedback(null);
    setTimeout(() => document.getElementById(buttonId)?.focus(), 0);
  };

  const handleCloseModalAction = () => {
    setModalAction(null);
  };

  const openModalFeedback = (
    icon: 'error' | 'success' | 'warning',
    title: string,
    subtitle: string,
  ) => {
    setModalFeedback({
      icon,
      title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: closeModalFeedback,
      buttonAction: closeModalFeedback,
    });
  };

  const openModalDeleteAnexo = (index: number) => {
    const anexo = anexos[index];
    if (!anexo) return;

    setModalAction({
      title: 'Excluir anexo',
      subTitle: `Tem certeza que deseja excluir o anexo ${anexo.nomeArquivo}?`,
      textOutlinedButton: 'Cancelar',
      textContainedButton: 'Excluir anexo',
      handleContainedButton: () => removerAnexo(index),
      handleOutlinedButton: handleCloseModalAction,
      handleShowModal: handleCloseModalAction,
      icon: 'warning',
    });
  };

  const removerAnexo = (index: number) => {
    onAnexosChange(anexos.filter((_, i) => i !== index));
    handleCloseModalAction();
  };

  const limparInputs = () => {
    [fileInputRef, pdfInputRef, cameraInputRef].forEach((ref) => {
      if (ref.current) {
        ref.current.value = '';
      }
    });
  };

  const validarArquivo = (file: File): boolean => {
    if (file.size > MAX_ANEXO_BYTES) {
      openModalFeedback(
        'warning',
        '',
        'O arquivo que você tentou enviar é maior que 10 MB. Por favor, escolha um arquivo menor.',
      );
      return false;
    }

    const temExtensaoValida = ANEXO_EXTENSIONS_REGEX.test(file.name);
    const temMimeValido = ANEXO_MIME_TYPES.includes(
      file.type as (typeof ANEXO_MIME_TYPES)[number],
    );

    if (!temExtensaoValida && !temMimeValido) {
      openModalFeedback(
        'warning',
        '',
        'Somente são aceitos arquivos nos formatos .pdf, .jpg e .png.',
      );
      return false;
    }

    return true;
  };

  const obterNomeArquivo = (file: File, indiceBase: number): string => {
    if (isArquivoCamera(file)) {
      return gerarNomeArquivoFoto(indiceBase);
    }
    return file.name;
  };

  const processarArquivos = async (files: FileList | File[]) => {
    const listaArquivos = Array.from(files);
    if (!listaArquivos.length) return;

    for (const file of listaArquivos) {
      if (!validarArquivo(file)) {
        limparInputs();
        return;
      }
    }

    const anexosAceitos: TranscricaoAnexo[] = [];

    try {
      for (let i = 0; i < listaArquivos.length; i++) {
        const file = listaArquivos[i];
        const nomeArquivo = obterNomeArquivo(file, anexos.length + anexosAceitos.length);

        if (anexoJaExiste(nomeArquivo, file.size, [...anexos, ...anexosAceitos])) {
          openModalFeedback('warning', '', MSG_ARQUIVO_DUPLICADO);
          if (anexosAceitos.length > 0) {
            onAnexosChange([...anexos, ...anexosAceitos]);
          }
          return;
        }

        const { prefix, base64Data } = await lerArquivoComoBase64(file);
        anexosAceitos.push({
          nomeArquivo,
          arquivo: base64Data,
          tipoArquivo: getFileExtension(prefix),
          tamanhoBytes: file.size,
        });
      }

      if (anexosAceitos.length > 0) {
        onAnexosChange([...anexos, ...anexosAceitos]);
      }
    } catch {
      openModalFeedback('error', '', MSG_ERRO_LER);
      if (anexosAceitos.length > 0) {
        onAnexosChange([...anexos, ...anexosAceitos]);
      }
    } finally {
      limparInputs();
    }
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    void processarArquivos(files);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (isMobileLayout) return;

    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (isMobileLayout) return;

    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (isMobileLayout) return;

    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (!files?.length) return;

    void processarArquivos(files);
  };

  return (
    <>
      <div
        className={`transcricao-anexos-dropzone${isDragging ? ' transcricao-anexos-dropzone--dragging' : ''}${isMobileLayout ? ' transcricao-anexos-dropzone--mobile' : ''}`}
        style={accessibility.contrastBorderAccessibility}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <FontAwesomeIcon
          icon={faArrowUpFromBracket}
          className="transcricao-anexos-dropzone-icon"
          style={accessibility.textStyleAccessibility}
        />

        <Typography
          className="transcricao-anexos-dropzone-texto"
          style={accessibility.textStyleAccessibility}
        >
          {isMobileLayout
            ? 'Fotografe a solicitação médica ou anexe um PDF'
            : 'Anexe a solicitação médica ou arraste e solte'}
        </Typography>

        {isMobileLayout ? (
          <div className="transcricao-anexos-upload-actions">
            <DefaultButton
              id={buttonId}
              variant="contained"
              startIcon={
                <FontAwesomeIcon
                  icon={faCamera}
                  style={accessibility.buttonTextStyleAccessibility}
                />
              }
              component="label"
              role={undefined}
              className="transcricao-anexos-upload-button"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.click();
              }}
            >
              <b style={accessibility.buttonTextStyleAccessibility} aria-hidden="true">
                TIRAR FOTO
              </b>
              <VisuallyHiddenInput
                ref={cameraInputRef}
                type="file"
                accept={ANEXO_ACCEPT_IMAGEM}
                capture="environment"
                onChange={handleFileInputChange}
                tabIndex={-1}
                aria-label="Tirar foto da solicitação médica"
              />
            </DefaultButton>

            <DefaultButton
              id={pdfButtonId}
              variant="outlined"
              startIcon={
                <FontAwesomeIcon
                  icon={faCloudDownloadAlt}
                  style={accessibility.buttonTextStyleAccessibility}
                />
              }
              component="label"
              role={undefined}
              className="transcricao-anexos-upload-button"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.click();
              }}
            >
              <b style={accessibility.buttonTextStyleAccessibility} aria-hidden="true">
                ANEXAR ARQUIVOS
              </b>
              <VisuallyHiddenInput
                ref={pdfInputRef}
                type="file"
                accept={ANEXO_ACCEPT_PDF}
                onChange={handleFileInputChange}
                tabIndex={-1}
                aria-label="Anexar arquivo PDF"
              />
            </DefaultButton>
          </div>
        ) : (
          <DefaultButton
            id={buttonId}
            variant="contained"
            startIcon={
              <FontAwesomeIcon
                icon={faCloudDownloadAlt}
                style={accessibility.buttonTextStyleAccessibility}
              />
            }
            component="label"
            role={undefined}
            className="transcricao-anexos-upload-button"
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.click();
            }}
          >
            <b style={accessibility.buttonTextStyleAccessibility} aria-hidden="true">
              ANEXAR ARQUIVOS
            </b>
            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              accept={ANEXO_ACCEPT}
              onChange={handleFileInputChange}
              multiple
              tabIndex={-1}
              aria-label="Anexar arquivos"
            />
          </DefaultButton>
        )}

        <Typography
          className="transcricao-anexos-formatos"
          style={accessibility.textStyleAccessibility}
        >
          Formatos suportados: PDF, JPG, PNG (máx. 10MB)
        </Typography>
      </div>

      {anexos.length > 0 && (
        <TranscricaoAnexosTable anexos={anexos} onExcluir={openModalDeleteAnexo} />
      )}

      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
      <ModalAction
        showModal={!!modalAction}
        handleShowModal={modalAction?.handleShowModal}
        icon={modalAction?.icon}
        textOutlinedButton={modalAction?.textOutlinedButton || ''}
        textContainedButton={modalAction?.textContainedButton || ''}
        title={modalAction?.title || ''}
        subTitle={modalAction?.subTitle || ''}
        handleOutlinedButton={modalAction?.handleOutlinedButton}
        handleContainedButton={modalAction?.handleContainedButton}
      />
    </>
  );
};

export default TranscricaoAnexosUploadSemValidacao;
